import React, { useRef, useState } from 'react';
import {
	autoUpdate,
	flip,
	FloatingPortal,
	offset,
	shift,
	useFloating,
	useInteractions
} from '@floating-ui/react';
import './EllipsisTooltipLabel.sass';

export default function EllipsisTooltipLabel({
	text,
	className,
	wrapperClassName = '',
	tooltipPlacement = 'bottom',
	useNativeTooltip = false,
	style
}) {
	const innerRef = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const displayText = text == null ? '' : String(text);

	const measureOverflow = () => {
		const el = innerRef.current;
		if (!el) return false;
		return el.scrollWidth > el.clientWidth + 0.5;
	};

	const showTooltip = isOverflowing && !useNativeTooltip;
	const { refs, floatingStyles } = useFloating({
		open: isOpen && showTooltip,
		onOpenChange: setIsOpen,
		placement: tooltipPlacement,
		middleware: [offset(8), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate
	});

	const { getFloatingProps } = useInteractions([]);

	const handlePointerEnter = () => {
		const overflowing = measureOverflow();
		setIsOverflowing(overflowing);
		if (overflowing && !useNativeTooltip) {
			setIsOpen(true);
		}
	};

	const handlePointerLeave = () => {
		setIsOpen(false);
	};

	if (useNativeTooltip) {
		return (
			<span className={wrapperClassName.trim() || undefined}>
				<span
					ref={innerRef}
					className={`${className || ''} ${isOverflowing ? 'is-truncated' : ''}`.trim()}
					style={style}
					title={isOverflowing ? displayText : undefined}
					onPointerEnter={handlePointerEnter}>
					{displayText}
				</span>
			</span>
		);
	}

	return (
		<>
			<span
				ref={refs.setReference}
				className={wrapperClassName.trim() || undefined}
				onPointerEnter={handlePointerEnter}
				onPointerLeave={handlePointerLeave}>
				<span
					ref={innerRef}
					className={`${className || ''} ${isOverflowing ? 'is-truncated' : ''}`.trim()}
					style={style}>
					{displayText}
				</span>
			</span>
			{isOpen && showTooltip && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						className="ellipsis-tooltip-label__tooltip"
						role="tooltip"
						{...getFloatingProps()}>
						{displayText}
					</div>
				</FloatingPortal>
			)}
		</>
	);
}
